import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, CalendarX, Pencil } from "lucide-react";

const vacio = { sede_id: "", nombre: "", tipo: "feriado", fecha_inicio: "", fecha_fin: "" };

export const Cierres = ({ sedes, onCambio, soloLectura = false }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(vacio);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/cierres").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const sedeName = (id) => sedes.find((s) => s.id === id)?.nombre || "—";

  const guardar = async () => {
    if (!form.sede_id || !form.nombre || !form.fecha_inicio) return toast.error("Completa sede, nombre y fecha");
    try {
      const body = { ...form, fecha_fin: form.fecha_fin || form.fecha_inicio };
      const { data } = editId
        ? await api.put(`/cierres/${editId}`, body)
        : await api.post("/cierres", body);
      toast.success(data.sesiones_retiradas
        ? `Guardado · ${data.sesiones_retiradas} clases retiradas del calendario`
        : "Días no laborables guardados");
      setOpen(false);
      setForm(vacio);
      setEditId(null);
      load();
      onCambio?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const borrar = async (id) => {
    await api.delete(`/cierres/${id}`);
    toast.success("Eliminado");
    load();
    onCambio?.();
  };

  return (
    <div className="mt-12" data-testid="cierres-seccion">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display text-2xl font-extrabold">Festivos y vacaciones</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {soloLectura
              ? "Los días marcados no tienen clase. Los gestiona administración."
              : "Por sede. No se generan clases en esos días y las ya creadas se cancelan."}
          </p>
        </div>
        {!soloLectura && (
          <Button className="rounded-full font-semibold" data-testid="add-cierre-btn"
            onClick={() => { setForm(vacio); setEditId(null); setOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Añadir
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((c) => (
          <Card key={c.id} className="rounded-2xl p-5 border-border shadow-sm" data-testid={`cierre-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <CalendarX className="h-4 w-4 text-primary" />
                  <p className="font-display font-bold">{c.nombre}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {sedeName(c.sede_id)} · {c.fecha_inicio}{c.fecha_fin !== c.fecha_inicio ? ` → ${c.fecha_fin}` : ""}
                </p>
              </div>
              <Badge variant={c.tipo === "vacaciones" ? "default" : "secondary"} className="capitalize">{c.tipo}</Badge>
            </div>
            <div className="flex gap-2 mt-4">
              {!soloLectura && (
                <>
              <Button variant="outline" size="sm" className="rounded-lg" data-testid={`edit-cierre-${c.id}`}
                onClick={() => {
                  setForm({ sede_id: c.sede_id, nombre: c.nombre, tipo: c.tipo,
                    fecha_inicio: c.fecha_inicio, fecha_fin: c.fecha_fin });
                  setEditId(c.id);
                  setOpen(true);
                }}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg text-destructive"
                onClick={() => borrar(c.id)} data-testid={`delete-cierre-${c.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
                </>
              )}
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="cierres-empty">
            No hay festivos ni vacaciones registrados.
          </p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl" data-testid="cierre-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">
            {editId ? "Editar festivo o vacaciones" : "Festivo o vacaciones"}
          </DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)}
                placeholder="Feria de Málaga" data-testid="cierre-nombre-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Sede</Label>
                <Select value={form.sede_id} onValueChange={(v) => set("sede_id", v)}>
                  <SelectTrigger data-testid="cierre-sede-select"><SelectValue placeholder="Selecciona sede" /></SelectTrigger>
                  <SelectContent>
                    {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
                  <SelectTrigger data-testid="cierre-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feriado">Festivo</SelectItem>
                    <SelectItem value="vacaciones">Vacaciones</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Desde</Label>
                <Input type="date" value={form.fecha_inicio} onChange={(e) => set("fecha_inicio", e.target.value)} data-testid="cierre-desde-input" />
              </div>
              <div className="grid gap-2">
                <Label>Hasta (opcional)</Label>
                <Input type="date" value={form.fecha_fin} onChange={(e) => set("fecha_fin", e.target.value)} data-testid="cierre-hasta-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={guardar} data-testid="save-cierre-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
